import { useQuery } from "@tanstack/react-query";
import type { StoredEvent } from "@facade/dashboard-shared";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EventStream, KindChips } from "../components/EventStream";
import { ModelQueryDrawer } from "../components/ModelQueryDrawer";
import { Shot } from "../components/Shot";
import { api } from "../lib/api";
import { fmtAgo, fmtDateTime, fmtMs, fmtNum, shortAct, shortFp, shotRef, trunc } from "../lib/format";
import { useLive, useLiveStore } from "../lib/ws";

export function DeviceLive() {
  const { deviceId = "" } = useParams();
  const dev = useQuery({ queryKey: ["device", deviceId], queryFn: () => api.device(deviceId), refetchInterval: 10_000 });
  const runs = useQuery({ queryKey: ["runs", deviceId], queryFn: () => api.runs(deviceId, 20), refetchInterval: 10_000 });
  const loops = useQuery({ queryKey: ["loops", deviceId], queryFn: () => api.loops(deviceId), refetchInterval: 10_000 });
  const live = useLive({ deviceId }, { limit: 400 });
  const [paused, setPaused] = useState(false);
  const [frozen, setFrozen] = useState<StoredEvent[]>([]);
  const [ns, setNs] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState<StoredEvent | null>(null);
  const presence = useLiveStore((s) => s.devices[deviceId]);
  const rs = useLiveStore((s) => s.runStatus[deviceId]);

  const events = paused ? frozen : live;
  const now = useMemo(() => {
    const latest = (k: string) => [...events].reverse().find((e) => e.kind === k);
    const tiles: Record<string, { value: unknown; ts: number }> = {};
    for (const e of events) {
      if (e.kind === "feed.sample" || e.kind === "harness.sample") {
        const p = e.payload as any;
        if (p?.label) tiles[p.label] = { value: p.value, ts: e.ts };
      }
    }
    return { shot: latest("screen.shot"), observe: latest("screen.observe"), runStart: latest("run.start"), runEnd: latest("run.end"), tiles };
  }, [events]);

  const currentRun = rs?.state === "running" ? rs : presence?.currentRunId ? { runId: presence.currentRunId, prompt: undefined, mode: undefined } : null;
  const online = presence?.online ?? dev.data?.online;

  return (
    <div className="stack">
      <div className="row">
        <h1 style={{ margin: 0 }}>
          <span className={`dot ${online ? "online" : ""}`} /> {presence?.nickname ?? dev.data?.nickname ?? deviceId}
        </h1>
        <span className="muted mono">{deviceId}</span>
        <span className="muted">{online ? "online" : `last seen ${fmtAgo(presence?.lastSeen ?? dev.data?.lastSeen)}`}</span>
        {dev.data?.stats && (
          <span className="stats" style={{ marginLeft: "auto" }}>
            <span className="stat"><span className="v">{fmtNum(dev.data.stats.events_last_minute)}</span><span className="l">events/min</span></span>
            <span className="stat"><span className="v">{fmtNum(dev.data.stats.runs)}</span><span className="l">runs</span></span>
            <span className="stat"><span className="v">{fmtNum(dev.data.stats.tokens_in_today + dev.data.stats.tokens_out_today)}</span><span className="l">tokens 24h</span></span>
          </span>
        )}
      </div>
      <div className="split">
        <div className="panel">
          <div className="toolbar">
            <KindChips value={ns} onChange={setNs} />
            <button
              className="small"
              style={{ marginLeft: "auto" }}
              onClick={() => {
                if (!paused) setFrozen(live);
                setPaused(!paused);
              }}
            >
              {paused ? "▶ resume" : "⏸ pause"}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              {events.length} buffered
            </span>
          </div>
          <EventStream events={events} deviceId={deviceId} ns={ns} onOpenQuery={setQuery} />
        </div>
        <div className="stack">
          <div className="panel">
            <h3>Now</h3>
            {currentRun ? (
              <div>
                <span className="badge running">running</span>{" "}
                <Link to={`/d/${deviceId}/runs/${encodeURIComponent(currentRun.runId)}`}>{currentRun.mode ?? ""} {currentRun.prompt ? JSON.stringify(trunc(currentRun.prompt, 80)) : currentRun.runId}</Link>
              </div>
            ) : now.runEnd ? (
              <div>
                <span className={`badge ${(now.runEnd.payload as any)?.ok ? "ok" : "fail"}`}>{(now.runEnd.payload as any)?.ok ? "last run ok" : "last run failed"}</span>{" "}
                {now.runEnd.runId && <Link to={`/d/${deviceId}/runs/${encodeURIComponent(now.runEnd.runId)}`}>{trunc((now.runStart?.payload as any)?.prompt ?? now.runEnd.runId, 60)}</Link>}
                <div className="muted" style={{ fontSize: 12 }}>{fmtAgo(now.runEnd.ts)} · {fmtMs((now.runEnd.payload as any)?.durationMs)}</div>
              </div>
            ) : (
              <div className="muted">idle</div>
            )}
          </div>
          <div className="panel">
            <h3>Screen</h3>
            <div className="row" style={{ alignItems: "flex-start" }}>
              {shotRef(now.shot?.payload) ? <Shot sha={shotRef(now.shot!.payload)!.sha} className="thumb" /> : <div className="muted">no screenshot yet</div>}
              {now.observe && (
                <dl className="kv">
                  <dt>activity</dt>
                  <dd>{shortAct((now.observe.payload as any).activity)}</dd>
                  <dt>pkg</dt>
                  <dd className="mono">{(now.observe.payload as any).pkg}</dd>
                  <dt>fp</dt>
                  <dd className="mono">{shortFp((now.observe.payload as any).fp)}</dd>
                  <dt>visible</dt>
                  <dd>{(now.observe.payload as any).summary?.visibleNodeCount ?? (now.observe.payload as any).sampleIds?.length ?? "?"}</dd>
                  <dt>dump</dt>
                  <dd>{fmtMs((now.observe.payload as any).summary?.dumpMs)}</dd>
                  <dt>at</dt>
                  <dd>{fmtAgo(now.observe.ts)}</dd>
                </dl>
              )}
            </div>
          </div>
          <div className="panel">
            <h3>Live tiles</h3>
            {Object.keys(now.tiles).length === 0 ? (
              <div className="muted">no samples</div>
            ) : (
              <div className="tiles">
                {Object.entries(now.tiles).map(([label, t]) => (
                  <div key={label} className="tile">
                    <div className="label">{label}</div>
                    <div className={`value ${t.value == null ? "null" : ""}`}>{t.value == null ? "null" : String(t.value)}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{fmtAgo(t.ts)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="panel">
            <h3>Loops</h3>
            {(loops.data ?? []).filter((l) => !l.killed_ts).length === 0 ? (
              <div className="muted">none running</div>
            ) : (
              <table>
                <tbody>
                  {(loops.data ?? [])
                    .filter((l) => !l.killed_ts)
                    .map((l) => (
                      <tr key={l.loop_id}>
                        <td className="mono">#{l.loop_id}</td>
                        <td>{l.label}</td>
                        <td className="muted">{l.ticks} ticks</td>
                        <td>{l.last_ok === 0 ? <span className="badge fail">last failed</span> : <span className="muted">{fmtAgo(l.last_tick_ts)}</span>}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
            <Link to={`/d/${deviceId}/feeds`} className="muted" style={{ fontSize: 12 }}>
              all loops & feeds →
            </Link>
          </div>
          <div className="panel">
            <h3>Recent runs</h3>
            {(runs.data ?? []).length === 0 ? (
              <div className="muted">no runs yet</div>
            ) : (
              <table>
                <tbody>
                  {(runs.data ?? []).map((r) => (
                    <tr key={r.run_id}>
                      <td>
                        {r.ended_ts == null ? <span className="badge running">run</span> : r.ok ? <span className="badge ok">ok</span> : <span className="badge fail">fail</span>}
                      </td>
                      <td>
                        <Link to={`/d/${deviceId}/runs/${encodeURIComponent(r.run_id)}`}>{trunc(r.prompt ?? r.run_id, 50)}</Link>
                        <div className="muted" style={{ fontSize: 11 }}>
                          {r.mode} · {fmtDateTime(r.started_ts)} · {r.model_calls} calls
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {query && <ModelQueryDrawer e={query} onClose={() => setQuery(null)} />}
    </div>
  );
}
